import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import EventTagChips from '../../../components/events/EventTagChips';
import { EVENT_TAG_IDS } from '../../../components/events/eventTagUtils';
import { eventTagAdminService, type AdminEventWithTags, type EventTagDefinition } from '../services/eventTags';

type TagFilter = 'all' | 'em_alta' | 'encerrado' | 'esgotado' | 'sem_tags';

const EventTagsAdminPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<EventTagDefinition[]>([]);
  const [events, setEvents] = useState<AdminEventWithTags[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<TagFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defsRes, eventsRes] = await Promise.all([
        eventTagAdminService.getDefinitions(),
        eventTagAdminService.listEvents(),
      ]);
      setDefinitions(defsRes.data ?? []);
      setEvents(eventsRes.data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleEmAlta = async (event: AdminEventWithTags, checked: boolean) => {
    setSavingId(event._id);
    setError(null);
    try {
      const res = await eventTagAdminService.patchEventTags(event._id, { emAlta: checked });
      if (res.data) {
        setEvents((prev) => prev.map((e) => (e._id === event._id ? res.data! : e)));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar');
    } finally {
      setSavingId(null);
    }
  };

  const systemDefs = definitions.filter((d) => d.type === 'system');
  const manualDefs = definitions.filter((d) => d.type === 'manual');

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((event) => {
      const matchesSearch =
        !q ||
        event.title.toLowerCase().includes(q) ||
        event.slug.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      const tagIds = new Set((event.tags ?? []).map((t) => t.id));
      const manual = new Set(event.manualEventTags ?? []);

      switch (filter) {
        case 'em_alta':
          return manual.has(EVENT_TAG_IDS.TRENDING);
        case 'encerrado':
          return tagIds.has(EVENT_TAG_IDS.ENDED);
        case 'esgotado':
          return tagIds.has(EVENT_TAG_IDS.SOLD_OUT);
        case 'sem_tags':
          return (event.tags ?? []).length === 0;
        default:
          return true;
      }
    });
  }, [events, search, filter]);

  const filterCounts = useMemo(() => {
    const counts = { all: events.length, em_alta: 0, encerrado: 0, esgotado: 0, sem_tags: 0 };
    for (const event of events) {
      const tagIds = new Set((event.tags ?? []).map((t) => t.id));
      const manual = new Set(event.manualEventTags ?? []);
      if (manual.has(EVENT_TAG_IDS.TRENDING)) counts.em_alta += 1;
      if (tagIds.has(EVENT_TAG_IDS.ENDED)) counts.encerrado += 1;
      if (tagIds.has(EVENT_TAG_IDS.SOLD_OUT)) counts.esgotado += 1;
      if ((event.tags ?? []).length === 0) counts.sem_tags += 1;
    }
    return counts;
  }, [events]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferIcon />
        Tags dos Eventos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Marque <strong>Em alta</strong> para destacar eventos na home. Tags de esgotado e encerrado são calculadas automaticamente.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {definitions.map((def) => (
          <EventTagChips
            key={def.id}
            tags={[{
              id: def.id,
              label: def.label,
              shortLabel: def.shortLabel,
              description: def.description,
              type: def.type,
              color: def.color,
              backgroundColor: def.backgroundColor,
            }]}
            max={1}
          />
        ))}
      </Stack>

      {systemDefs.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Automáticas: {systemDefs.map((d) => d.label).join(' · ')} — por data de fim e estoque de ingressos.
          Eventos encerrados mostram só a tag <strong>Encerrado</strong> no card.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por nome ou slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Tabs
        value={filter}
        onChange={(_, v) => setFilter(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Todos (${filterCounts.all})`} value="all" />
        <Tab label={`Em alta (${filterCounts.em_alta})`} value="em_alta" />
        <Tab label={`Encerrados (${filterCounts.encerrado})`} value="encerrado" />
        <Tab label={`Esgotados (${filterCounts.esgotado})`} value="esgotado" />
        <Tab label={`Sem tags (${filterCounts.sem_tags})`} value="sem_tags" />
      </Tabs>

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : filteredEvents.length === 0 ? (
        <Alert severity="warning">Nenhum evento encontrado com os filtros atuais.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Preview no card</TableCell>
                {manualDefs.map((d) => (
                  <TableCell key={d.id} align="center">{d.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents.map((event) => {
                const isEmAlta = (event.manualEventTags || []).includes(EVENT_TAG_IDS.TRENDING);
                const isSaving = savingId === event._id;
                return (
                  <TableRow key={event._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{event.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{event.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      {(event.tags || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Sem tags</Typography>
                      ) : (
                        <EventTagChips tags={event.tags} compact max={2} />
                      )}
                    </TableCell>
                    {manualDefs.map((d) => (
                      <TableCell key={d.id} align="center">
                        {d.id === EVENT_TAG_IDS.TRENDING ? (
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isEmAlta}
                                disabled={isSaving}
                                onChange={(_, checked) => handleToggleEmAlta(event, checked)}
                              />
                            }
                            label={isSaving ? 'Salvando…' : isEmAlta ? 'Ativo' : 'Inativo'}
                            sx={{ m: 0, gap: 0.5 }}
                          />
                        ) : null}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default EventTagsAdminPanel;
